import {clearStoredLogs} from './logStorage';

type Reporter = (level: 'warn' | 'error', message: string, context: Record<string, unknown>) => void;

// The logger registers itself here at load. It cannot be imported directly: its
// import chain cycles back into modules that call setChatStorageItem.
let report: Reporter = (level, message, context) => console[level](message, context);

export const setChatStorageReporter = (reporter: Reporter): void => {
    report = reporter;
};

export class ChatStorageError extends Error {
    readonly key: string;

    constructor(key: string, cause: unknown) {
        const reason = cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause);
        super(`Browser storage write failed for "${key}": ${reason}`);
        this.name = 'ChatStorageError';
        this.key = key;
        this.cause = cause;
    }
}

export const isStorageQuotaError = (error: unknown): boolean => {
    const e = error as {name?: string; code?: number} | null;
    return e?.name === 'QuotaExceededError' ||
        e?.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        e?.code === 22 ||
        e?.code === 1014;
};

/**
 * localStorage.setItem for keys chat cannot work without. On quota failure it
 * clears our own offline logs once and retries; any remaining failure is logged
 * with the key and thrown, so init fails visibly instead of half-working.
 */
export function setChatStorageItem(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
        return;
    } catch (firstError) {
        if (isStorageQuotaError(firstError)) {
            clearStoredLogs();
            try {
                localStorage.setItem(key, value);
                report('warn', 'Chat storage write succeeded after clearing offline logs', {
                    source: 'chat',
                    event: 'chat_storage_quota_recovered',
                    storageKey: key,
                });
                return;
            } catch (retryError) {
                throw reportAndWrap(key, retryError, true);
            }
        }
        throw reportAndWrap(key, firstError, false);
    }
}

function reportAndWrap(key: string, cause: unknown, afterLogClear: boolean): ChatStorageError {
    const wrapped = new ChatStorageError(key, cause);
    report('error', 'Chat storage write failed', {
        source: 'chat',
        event: 'chat_storage_write_failed',
        storageKey: key,
        quotaExceeded: isStorageQuotaError(cause),
        afterLogClear,
        error: wrapped,
    });
    return wrapped;
}
